/**
 * Discovery banner for the Pro Coaching Studio feature.
 * Renders whenever the caller is Pro-eligible (parent decides), and
 * dismisses via localStorage so it doesn't nag on every visit.
 *
 * If the user already has coached clips OR they've dismissed the banner,
 * we hide it. If they have no clips yet, we still show it (as a preview)
 * with the Try button disabled and a hint to upload first.
 */
import { useState } from "react";
import { Presentation, X } from "lucide-react";

const KEY = "coaching_banner_dismissed_v1";

export default function CoachingBanner({ clips = [], onTry, uploadHref = "/portal/studio" }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const hasCoached = clips.some((c) => c.kind === "coached");
  if (dismissed || hasCoached) return null;
  // Prefer an already-owned/purchased clip so "Try it" definitely works.
  const firstClip =
    clips.find((c) => c.kind !== "coached" && (c.paid || c.purchased || c.owner_user_id))
    || clips.find((c) => c.kind !== "coached");

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="relative border border-orange-700 bg-gradient-to-r from-orange-600/15 via-orange-600/10 to-transparent p-4 mb-4"
      data-testid="coaching-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 text-stone-500 hover:text-stone-100"
        title="Dismiss"
        data-testid="coaching-banner-dismiss"
      >
        <X size={13} />
      </button>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pr-6">
        <div className="shrink-0 h-10 w-10 rounded-full bg-orange-600/20 border border-orange-700 flex items-center justify-center">
          <Presentation size={18} className="text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-500 mb-0.5">New · Pro feature</div>
          <div className="font-display uppercase text-lg font-bold text-stone-100 leading-tight">Try Coaching Studio</div>
          <div className="text-xs text-stone-300 mt-1">
            Record yourself analyzing a run — face bubble + voiceover + on-screen timer, baked into one shareable video.
            {firstClip ? (
              <span className="hidden sm:inline"> Great for teaching cowboys their reps.</span>
            ) : (
              <span> Upload a video in Rodeo Box first, then hit Coach on any clip you make.</span>
            )}
          </div>
        </div>
        {firstClip ? (
          <button
            onClick={() => onTry(firstClip)}
            className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2 shrink-0"
            data-testid="coaching-banner-try"
          >
            <Presentation size={13} /> Try it
          </button>
        ) : (
          <a
            href={uploadHref}
            className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2 shrink-0"
            data-testid="coaching-banner-upload"
          >
            <Presentation size={13} /> Start
          </a>
        )}
      </div>
    </div>
  );
}
